import { useState, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';
import toast from 'react-hot-toast';
import { ImportResult, Category, ExcelMappedRow } from '../types';
import { apiService } from '../services/api';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB
const PREVIEW_ROWS = 5;
const OPTION_NONE = '__none__';

interface ExcelImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type CategorySource = 'none' | 'column' | 'fixed';

interface ColumnMapping {
  nome: string;
  telefone: string;
  email: string;
  observacoes: string;
}

function escapeCsvValue(value: string): string {
  const s = String(value ?? '').trim();
  if (s.includes(',') || s.includes('"') || s.includes('\n') || s.includes('\r')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function buildCsvFromMappedRows(rows: ExcelMappedRow[]): string {
  const header = 'nome,telefone,email,observacoes,categoriaId';
  const lines = rows.map((r) =>
    [
      escapeCsvValue(r.nome),
      escapeCsvValue(r.telefone),
      escapeCsvValue(r.email ?? ''),
      escapeCsvValue(r.observacoes ?? ''),
      escapeCsvValue(r.categoriaId ?? ''),
    ].join(',')
  );
  return [header, ...lines].join('\r\n');
}

export function ExcelImportModal({ isOpen, onClose, onSuccess }: ExcelImportModalProps) {
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [file, setFile] = useState<File | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<unknown[][]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [mapping, setMapping] = useState<ColumnMapping>({ nome: '', telefone: '', email: '', observacoes: '' });
  const [categorySource, setCategorySource] = useState<CategorySource>('fixed');
  const [categoryHeader, setCategoryHeader] = useState('');
  const [fixedCategoryId, setFixedCategoryId] = useState<string | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const displayHeaders = headers.map((h, i) => (h != null && String(h).trim() !== '') ? String(h).trim() : `Coluna ${String.fromCharCode(65 + i)}`);

  const loadCategories = useCallback(async () => {
    try {
      const list = await apiService.getAllCategories();
      setCategories(list);
    } catch {
      toast.error('Não foi possível carregar as categorias');
    }
  }, []);

  useEffect(() => {
    if (isOpen) loadCategories();
  }, [isOpen, loadCategories]);

  const getCellValue = useCallback((row: unknown[], headerKey: string): string => {
    if (!headerKey || headerKey === OPTION_NONE) return '';
    const idx = displayHeaders.indexOf(headerKey);
    if (idx < 0) return '';
    const cell = row[idx];
    return cell != null ? String(cell).trim() : '';
  }, [displayHeaders]);

  const resolveCategoryId = useCallback((cellValue: string): string => {
    if (!cellValue) return '';
    const byName = categories.find((c) => c.nome.toLowerCase() === cellValue.toLowerCase());
    return byName ? String(byName.id) : cellValue;
  }, [categories]);

  const buildMappedRows = useCallback((): ExcelMappedRow[] => {
    const result: ExcelMappedRow[] = [];
    for (const row of rows) {
      const nome = getCellValue(row, mapping.nome);
      const telefone = getCellValue(row, mapping.telefone);
      if (!nome || !telefone) continue;
      let categoriaId = '';
      if (categorySource === 'fixed' && fixedCategoryId) {
        categoriaId = fixedCategoryId;
      } else if (categorySource === 'column' && categoryHeader) {
        categoriaId = resolveCategoryId(getCellValue(row, categoryHeader));
      }
      result.push({
        nome,
        telefone,
        email: mapping.email ? getCellValue(row, mapping.email) || undefined : undefined,
        observacoes: mapping.observacoes ? getCellValue(row, mapping.observacoes) || undefined : undefined,
        categoriaId: categoriaId || undefined,
      });
    }
    return result;
  }, [rows, mapping, categorySource, categoryHeader, fixedCategoryId, getCellValue, resolveCategoryId]);

  const parseFile = useCallback((file: File) => {
    setParseError(null);
    const ext = file.name.toLowerCase().split('.').pop();
    if (ext !== 'xls' && ext !== 'xlsx') {
      toast.error('Selecione um arquivo Excel (.xls ou .xlsx)');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      toast.error('Arquivo muito grande. Máximo 5 MB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        if (!data || !(data instanceof ArrayBuffer)) {
          setParseError('Falha ao ler o arquivo');
          return;
        }
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        if (!firstSheetName) { setParseError('Planilha vazia'); return; }
        const sheet = workbook.Sheets[firstSheetName];
        const raw: unknown[][] = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
        if (!raw.length) { setParseError('Nenhum dado na planilha'); return; }
        const headerRow = raw[0].map((c) => (c != null ? String(c) : ''));
        setHeaders(headerRow);
        const dataRows = raw.slice(1).filter((row) => row.some((c) => c != null && String(c).trim() !== ''));
        setRows(dataRows);
        setFile(file);
        setStep(2);
        setMapping({ nome: '', telefone: '', email: '', observacoes: '' });
        setCategorySource('fixed');
        setCategoryHeader('');
        setFixedCategoryId(null);
        setImportResult(null);
      } catch (err) {
        setParseError(err instanceof Error ? err.message : 'Erro ao ler planilha');
        toast.error(err instanceof Error ? err.message : 'Erro ao ler planilha');
      }
    };
    reader.readAsArrayBuffer(file);
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) parseFile(f);
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) parseFile(f);
  };

  const canGoToPreview = mapping.nome && mapping.telefone && (categorySource !== 'column' || categoryHeader) && (categorySource !== 'fixed' || fixedCategoryId != null);

  const goToPreview = () => {
    if (!canGoToPreview) return;
    if (categorySource === 'column' && !categoryHeader) {
      toast.error('Selecione a coluna de categoria ou use categoria fixa');
      return;
    }
    if (categorySource === 'fixed' && !fixedCategoryId) {
      toast.error('Selecione uma categoria fixa');
      return;
    }
    setStep(3);
  };

  const previewRows = buildMappedRows();
  const previewSample = previewRows.slice(0, PREVIEW_ROWS);

  const getCategoryDisplayLabel = useCallback((categoriaId: string | undefined): string => {
    if (!categoriaId) return '—';
    const cat = categories.find((c) => String(c.id) === categoriaId);
    return cat ? `${cat.nome}` : categoriaId;
  }, [categories]);

  const handleConfirmImport = async () => {
    if (previewRows.length === 0) {
      toast.error('Nenhuma linha válida para importar (nome e telefone obrigatórios)');
      return;
    }
    setIsUploading(true);
    setImportResult(null);
    try {
      const csv = buildCsvFromMappedRows(previewRows);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const csvFile = new File([blob], 'importacao-excel.csv', { type: 'text/csv' });
      const result = await apiService.importCSV(csvFile);
      setImportResult(result);
      setStep(4);
      if (result.success) {
        toast.success(`Importação concluída! ${result.successfulImports} contatos importados.`);
        onSuccess();
      } else {
        toast(`Importação parcial: ${result.successfulImports} sucessos, ${result.failedImports} erros.`, { icon: '⚠️' });
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro ao importar';
      toast.error(msg);
      setImportResult({ success: false, totalRows: previewRows.length, successfulImports: 0, failedImports: previewRows.length, errors: [msg] });
      setStep(4);
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setFile(null);
    setHeaders([]);
    setRows([]);
    setParseError(null);
    setMapping({ nome: '', telefone: '', email: '', observacoes: '' });
    setCategorySource('fixed');
    setCategoryHeader('');
    setFixedCategoryId(null);
    setImportResult(null);
    onClose();
  };

  const handleBack = () => {
    if (step === 2) setStep(1);
    else if (step === 3) setStep(2);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-3xl max-h-[calc(100vh-2rem)] overflow-y-auto border border-gray-100" role="dialog" aria-labelledby="excel-import-title">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center flex-shrink-0">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
          </div>
          <div>
            <h2 id="excel-import-title" className="text-xl font-bold text-gray-900">Importar Contatos via Excel</h2>
            <p className="text-sm text-gray-500">Passo {step} de 4 {step === 1 && '— Selecionar arquivo'} {step === 2 && '— Mapear colunas'} {step === 3 && '— Conferir preview'} {step === 4 && '— Resultado'}</p>
          </div>
        </div>

        {step === 1 && (
          <>
            <div onDrop={handleDrop} onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }} onDragLeave={() => setIsDragging(false)} className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${isDragging ? 'border-green-500 bg-green-50' : 'border-gray-300 hover:border-green-400'}`}>
              <input id="excel-file" type="file" accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={handleFileChange} className="hidden" />
              <label htmlFor="excel-file" className="cursor-pointer block">
                <div className="w-14 h-14 bg-gray-100 rounded-xl mx-auto mb-3 flex items-center justify-center"><svg className="w-7 h-7 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" /></svg></div>
                <p className="text-gray-700 font-medium">Clique para selecionar ou arraste o arquivo aqui</p>
                <p className="text-sm text-gray-500 mt-1">Arquivos .xls ou .xlsx (máx. 5 MB)</p>
              </label>
            </div>
            {parseError && <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{parseError}</div>}
          </>
        )}

        {step === 2 && (
          <div className="space-y-4">
            {file && <p className="text-sm text-gray-600">Arquivo: <strong>{file.name}</strong></p>}
            <p className="text-sm text-gray-700">Associe cada coluna ao campo. <strong>Nome</strong> e <strong>telefone</strong> são obrigatórios.</p>
            <div className="grid gap-4 sm:grid-cols-2">
              {(['nome', 'telefone', 'email', 'observacoes'] as const).map((field) => (
                <div key={field}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{field === 'nome' ? 'Nome *' : field === 'telefone' ? 'Telefone *' : field === 'email' ? 'Email' : 'Observações'}</label>
                  <select value={mapping[field]} onChange={(e) => setMapping((m) => ({ ...m, [field]: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                    <option value="">— {field === 'nome' || field === 'telefone' ? 'Selecione —' : 'Não importar —'}</option>
                    {displayHeaders.map((h) => (<option key={h} value={h}>{h}</option>))}
                  </select>
                </div>
              ))}
            </div>
            <div className="border-t pt-4 space-y-3">
              <label className="block text-sm font-medium text-gray-700">Categoria</label>
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2">
                  <input type="radio" name="catSource" checked={categorySource === 'column'} onChange={() => setCategorySource('column')} className="rounded" />
                  <span className="text-sm">Coluna da planilha</span>
                </label>
                {categorySource === 'column' && (
                  <select value={categoryHeader} onChange={(e) => setCategoryHeader(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm">
                    <option value="">— Selecione —</option>
                    {displayHeaders.map((h) => (<option key={h} value={h}>{h}</option>))}
                  </select>
                )}
                <label className="flex items-center gap-2">
                  <input type="radio" name="catSource" checked={categorySource === 'fixed'} onChange={() => setCategorySource('fixed')} className="rounded" />
                  <span className="text-sm">Categoria fixa</span>
                </label>
                {categorySource === 'fixed' && (
                  <select value={fixedCategoryId ?? ''} onChange={(e) => setFixedCategoryId(e.target.value ? e.target.value : null)} className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm">
                    <option value="">— Selecione —</option>
                    {categories.map((c) => (<option key={c.id} value={String(c.id)}>{c.nome}</option>))}
                  </select>
                )}
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">Total de linhas a importar: <strong>{previewRows.length}</strong></p>
            <div className="overflow-x-auto border border-gray-200 rounded-lg">
              <table className="min-w-full text-sm">
                <thead><tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left py-2 px-3 font-medium text-gray-700">Nome</th>
                  <th className="text-left py-2 px-3 font-medium text-gray-700">Telefone</th>
                  <th className="text-left py-2 px-3 font-medium text-gray-700">Email</th>
                  <th className="text-left py-2 px-3 font-medium text-gray-700">Categoria</th>
                  <th className="text-left py-2 px-3 font-medium text-gray-700">Observações</th>
                </tr></thead>
                <tbody>
                  {previewSample.map((r, i) => (
                    <tr key={i} className="border-b border-gray-100 last:border-0">
                      <td className="py-2 px-3">{r.nome}</td>
                      <td className="py-2 px-3">{r.telefone}</td>
                      <td className="py-2 px-3">{r.email ?? '—'}</td>
                      <td className="py-2 px-3">{getCategoryDisplayLabel(r.categoriaId)}</td>
                      <td className="py-2 px-3 max-w-[120px] truncate" title={r.observacoes}>{r.observacoes ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {previewRows.length > PREVIEW_ROWS && <p className="text-xs text-gray-500">Exibindo as primeiras {PREVIEW_ROWS} de {previewRows.length} linhas.</p>}
          </div>
        )}

        {step === 4 && importResult && (
          <div className={`border rounded-xl p-6 ${importResult.success ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
            <div className="flex items-center gap-3 mb-4">
              <h3 className={`font-semibold text-lg ${importResult.success ? 'text-green-900' : 'text-yellow-900'}`}>Resultado da importação</h3>
            </div>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="bg-white rounded-lg p-3 text-center"><div className="text-2xl font-bold text-gray-900">{importResult.totalRows}</div><div className="text-sm text-gray-600">Total</div></div>
              <div className="bg-white rounded-lg p-3 text-center"><div className="text-2xl font-bold text-green-600">{importResult.successfulImports}</div><div className="text-sm text-gray-600">Sucessos</div></div>
              <div className="bg-white rounded-lg p-3 text-center"><div className="text-2xl font-bold text-red-600">{importResult.failedImports}</div><div className="text-sm text-gray-600">Erros</div></div>
            </div>
            {importResult.errors?.length > 0 && (
              <details className="cursor-pointer">
                <summary className="text-sm font-semibold text-red-700">Ver erros ({importResult.errors.length})</summary>
                <div className="mt-2 bg-red-50 border border-red-200 rounded-lg p-3 max-h-40 overflow-y-auto">
                  {importResult.errors.map((err, i) => (<div key={i} className="text-sm text-red-600 py-1 border-b border-red-100 last:border-0">{err}</div>))}
                </div>
              </details>
            )}
          </div>
        )}

        <div className="flex gap-3 mt-6 pt-6 border-t border-gray-100">
          {step === 1 && <button type="button" onClick={handleClose} className="flex-1 py-2.5 px-4 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 font-medium">Fechar</button>}
          {step === 2 && (
            <>
              <button type="button" onClick={handleBack} className="py-2.5 px-4 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 font-medium">Voltar</button>
              <button type="button" onClick={goToPreview} disabled={!canGoToPreview} className="flex-1 py-2.5 px-4 btn-primary disabled:opacity-50 disabled:cursor-not-allowed font-medium">Pré-Visualizar</button>
            </>
          )}
          {step === 3 && (
            <>
              <button type="button" onClick={handleBack} className="py-2.5 px-4 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 font-medium">Voltar</button>
              <button type="button" onClick={handleConfirmImport} disabled={isUploading || previewRows.length === 0} className="flex-1 py-2.5 px-4 btn-primary disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2">
                {isUploading ? <> <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg> Importando...</> : 'Confirmar importação'}
              </button>
            </>
          )}
          {step === 4 && <button type="button" onClick={handleClose} className="flex-1 py-2.5 px-4 btn-primary font-medium">Fechar</button>}
        </div>
      </div>
    </div>
  );
}
